<script>
import {computed, ref} from 'vue' // Vue 2.7 ships the Composition API built in
import draggable from 'vuedraggable' // npm i vuedraggable@^2.24.3  (Vue 2 version)

export default {
    name: 'DualListSelector',
    components: {draggable},
    props: {
        /** v-model: ordered array of selected values */
        value: {type: Array, default: () => []},
        /** all items — array of objects OR plain strings */
        items: {type: Array, default: () => []},
        itemText: {type: String, default: 'name'},
        itemValue: {type: String, default: 'id'},
        leftTitle: {type: String, default: 'Disponible'},
        rightTitle: {type: String, default: 'Choisie'},
        height: {type: Number, default: 320},
        readonly: {type: Boolean, default: false}
    },
    setup(props, {emit}) {
        const search = ref('')
        const checked = ref([])
        
        /** Normalize every entry to { text, value } — strings use themselves for both */
        const normalize = (item) => {
            if (item !== null && typeof item === 'object') {
                return {text: item[props.itemText], value: item[props.itemValue]}
            }
            return {text: String(item), value: item}
        }
        
        const normalizedItems = computed(() => props.items.map(normalize))
        const valueSet = computed(() => new Set(props.value))
        const byValue = computed(() => {
            const m = new Map()
            normalizedItems.value.forEach(i => m.set(i.value, i))
            return m
        })
        
        /** left side: everything not selected */
        const available = computed(() =>
            normalizedItems.value.filter(i => !valueSet.value.has(i.value))
        )
        const filteredAvailable = computed(() => {
            const q = search.value.toLowerCase().trim()
            if (!q) return available.value
            return available.value.filter(i => String(i.text).toLowerCase().includes(q))
        })
        
        /** right side: get = items in v-model order · set = drag end → emit ordered values */
        const orderedSelection = computed({
            get: () => props.value.map(v => byValue.value.get(v)).filter(Boolean),
            set: (list) => emit('input', list.map(i => i.value))
        })
        
        const leftListStyle = computed(() => ({
            maxHeight: props.height + 'px', minHeight: props.height + 'px'
        }))
        const rightListStyle = computed(() => ({
            maxHeight: (props.height + 62) + 'px', minHeight: (props.height + 62) + 'px'
        }))
        
        const isChecked = (item) => checked.value.includes(item.value)
        const toggle = (item) => {
            const i = checked.value.indexOf(item.value)
            if (i === -1) checked.value.push(item.value)
            else checked.value.splice(i, 1)
        }
        const checkAllFiltered = () => {
            const vs = filteredAvailable.value.map(i => i.value)
            checked.value = Array.from(new Set([...checked.value, ...vs]))
        }
        const emitValues = (values) => emit('input', values)
        const addValues = (values) => {
            emitValues([...props.value, ...values.filter(v => !valueSet.value.has(v))])
            checked.value = checked.value.filter(v => !values.includes(v))
        }
        const removeValues = (values) => {
            emitValues(props.value.filter(v => !values.includes(v)))
        }
        const addChecked = () => {
            if (checked.value.length) addValues([...checked.value])
        }
        const addAll = () => addValues(filteredAvailable.value.map(i => i.value))
        
        return {
            search, checked,
            available, filteredAvailable, orderedSelection,
            leftListStyle, rightListStyle,
            isChecked, toggle, checkAllFiltered,
            emitValues, addValues, removeValues, addChecked, addAll
        }
    }
}
</script>

<template>
    <v-row class="dual-list-selector" no-gutters>
        <!-- ================= LEFT: not selected ================= -->
        <v-col cols="12" md="5">
            <v-card outlined class="fill-height d-flex flex-column">
                <w-toolbar color="blue" :title="leftTitle">
                    <template #right>
                        <w-chip :text="available.length"/>
                    </template>
                </w-toolbar>
                <v-divider/>
                <div class="pa-3 pb-2">
                    <w-text-field
                        v-model="search"
                        prepend-inner-icon="mdi-magnify"
                        placeholder="Filter items…"
                    />
                </div>
                <v-divider></v-divider>
                <v-list dense class="selector-list py-0" :style="leftListStyle">
                    <template v-if="filteredAvailable.length">
                        <v-list-item
                            v-for="item in filteredAvailable"
                            :key="item.value"
                            @click="toggle(item)"
                            @dblclick.stop="addValues([item.value])"
                        >
                            <v-list-item-action class="my-0 mr-3" style="min-width:24px;">
                                <v-checkbox
                                    dense
                                    hide-details
                                    class="my-0 py-0"
                                    style="pointer-events:none"
                                    :readonly="readonly"
                                    :input-value="isChecked(item)"
                                />
                            </v-list-item-action>
                            <v-list-item-content class="py-1">
                                <v-list-item-title>{{ item.text }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action class="my-0" style="min-width:24px;">
                                <w-btn-icon
                                    icon="mdi-plus"
                                    color="blue"
                                    title="Add"
                                    x-small
                                    :disabled="readonly"
                                    @press="addValues([item.value])"
                                />
                            </v-list-item-action>
                        </v-list-item>
                    </template>
                    <div v-else class="text-center grey--text py-10">
                        <v-icon large color="grey lighten-2">mdi-inbox-outline</v-icon>
                        <div class="caption mt-2">Rien ici</div>
                    </div>
                </v-list>
                <v-divider/>
                <div class="d-flex align-center px-3 py-2">
                    <w-btn
                        text-option
                        color="blue"
                        x-small
                        text="tout sélectionner"
                        :disabled="!filteredAvailable.length || readonly"
                        @press="checkAllFiltered"
                    />
                    <w-btn
                        text-option
                        x-small
                        text="effacer"
                        :disabled="!checked.length || readonly"
                        @press="checked = []"
                    />
                    <v-spacer/>
                    <span class="caption grey--text">{{ checked.length }} Vérifié(s)</span>
                </div>
            </v-card>
        </v-col>
        
        <!-- ================= MIDDLE: transfer buttons ================= -->
        <v-col cols="12" md="2" class="d-flex flex-md-column flex-row align-center justify-center">
            <w-btn
                icon="mdi-chevron-right"
                outlined
                color="blue"
                title="Add checked"
                :disabled="!checked.length || readonly"
                @press="addChecked"
            />
            <br/>
            <w-btn
                icon="mdi-chevron-double-right"
                outlined
                color="blue"
                title="Add checked"
                :disabled="!filteredAvailable.length || readonly"
                @press="addAll"
            />
            <br/>
            <w-btn
                icon="mdi-chevron-double-left"
                outlined
                color="blue"
                title="Add checked"
                :disabled="!value.length || readonly"
                @press="emitValues([])"
            />
        </v-col>
        
        <!-- ================= RIGHT: selected + drag-sortable ================= -->
        <v-col cols="12" md="5">
            <v-card outlined class="fill-height d-flex flex-column">
                <w-toolbar color="teal" :title="rightTitle">
                    <template #right>
                        <w-chip :text="value.length"/>
                    </template>
                </w-toolbar>
                <v-divider/>
                <v-list dense class="selector-list py-0 grow" :style="rightListStyle">
                    <draggable
                        v-model="orderedSelection"
                        handle=".drag-handle"
                        animation="150"
                        ghost-class="sortable-ghost"
                        drag-class="sortable-drag"
                    >
                        <v-list-item
                            v-for="(item, index) in orderedSelection"
                            :key="item.value"
                            @dblclick="removeValues([item.value])"
                        >
                            <v-list-item-action class="my-0 mr-1 drag-handle" style="min-width:24px;">
                                <v-icon color="grey">mdi-drag-vertical</v-icon>
                            </v-list-item-action>
                            <v-list-item-action class="my-0 mr-2" style="min-width:26px;">
                                <span class="order-badge">{{ index + 1 }}</span>
                            </v-list-item-action>
                            <v-list-item-content class="py-1">
                                <v-list-item-title>{{ item.text }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action class="my-0" style="min-width:24px;">
                                <w-btn-icon
                                    icon="mdi-close"
                                    color="grey darken-1"
                                    x-small
                                    :disabled="readonly"
                                    @press="removeValues([item.value])"
                                    title="Remove"
                                />
                            </v-list-item-action>
                        </v-list-item>
                    </draggable>
                    <div v-if="!orderedSelection.length" class="text-center grey--text py-10">
                        <v-icon large color="grey lighten-2">mdi-cursor-default-click-outline</v-icon>
                        <div class="caption mt-2">
                            Sélectionnez les éléments à gauche, puis faites-les glisser pour les ordonner.
                        </div>
                    </div>
                </v-list>
                <v-divider/>
                <div class="d-flex align-center px-3 py-2">
                  <span class="caption grey--text">
                    <v-icon x-small class="mr-1">mdi-drag-vertical</v-icon>
                      Faites glisser la poignée pour réorganiser
                  </span>
                    <v-spacer/>
                    <w-btn
                        text-option
                        color="red"
                        x-small
                        text="Tout effacer"
                        :disabled="!value.length || readonly"
                        @press="emitValues([])"
                    />
                </div>
            </v-card>
        </v-col>
    </v-row>
</template>

<style scoped>
.selector-list {
    overflow-y: auto;
}

.drag-handle {
    cursor: grab;
}

.drag-handle:active {
    cursor: grabbing;
}

.sortable-ghost {
    background: #E3F2FD !important;
}

.sortable-drag {
    box-shadow: 0 8px 20px rgba(0, 0, 0, .18) !important;
    background: #fff;
}

.order-badge {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #1976D2;
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.v-list-item {
    user-select: none;
}
</style>
